import { MutableRefObject } from 'react';

import { helpers } from '@helpers';
import { navigationService } from '@services';

import { EpisodeData, HelpersData, VideoPlayerReference } from './types';

export const onVideoEnd = (helpersData: HelpersData) => {
  const {
    vodID,
    setVodID,
    seasonIndex,
    setSeasonIndex,
    setEpisodeIndex,
    nextEpisodeData,
    setShowNextEpisodeOverlay,
    exitedShowNextEpisodeOverlay,
    currentVODPlaylistState,
    setVODPLaylist,
    videoPlayerReference,
    vodPlaylistReference,
    vodFirstFocusReference,
  } = helpersData;

  const shouldPrepareEpisode = shouldPrepareNextEpisode(helpersData);

  if (shouldPrepareEpisode) {
    getNextVideoData(helpersData);
  }

  if (!exitedShowNextEpisodeOverlay && nextEpisodeData && nextEpisodeData.vodID !== '') {
    if (nextEpisodeData.episodeIndex >= 0) {
      setEpisodeIndex(nextEpisodeData.episodeIndex);
    }

    if (nextEpisodeData.seasonIndex >= 0) {
      setSeasonIndex(nextEpisodeData.seasonIndex);
    }

    if (nextEpisodeData.vodID === vodID) {
      reloadVideo(videoPlayerReference);
    } else {
      // Set new current index for short form playlist drawer
      if (seasonIndex === -1) {
        setVODPLaylist({
          ...currentVODPlaylistState,
          currentID: nextEpisodeData.vodID,
        });

        // Hide playlist drawer when updating new video watching
        if (vodPlaylistReference?.current) {
          vodPlaylistReference.current?.hidePlayList();
          vodPlaylistReference.current?.hideControls();
        }
      }

      setVodID(nextEpisodeData.vodID);
      setShowNextEpisodeOverlay(false);
      helpers.FOCUS_NOW(vodFirstFocusReference);
    }
  } else {
    // Dismiss controls and navigate back to the previous screen
    if (vodPlaylistReference?.current) {
      vodPlaylistReference.current?.hidePlayList();
      vodPlaylistReference.current?.hideControls();
    }

    navigationService.goBack();
  }
};

export const getPreviousVideoData = (data: HelpersData) => getNextVideoData(data, true, true);

export const getNextVideoData = (
  helpersData: HelpersData,
  isPrevious = false,
  override = false,
): EpisodeData | undefined => {
  const { playlist, seasonIndex, episodeIndex, setNextEpisodeData, setShowNextEpisodeOverlay } = helpersData;

  const EPISODES = 'episodes';

  const nextEpisodeIndex = isPrevious ? episodeIndex - 1 : episodeIndex + 1;
  let nextData: EpisodeData = {
    vodID: '',
    episodeIndex: 0,
    seasonIndex: 0,
  };

  const shouldPrepareEpisode = shouldPrepareNextEpisode(helpersData);

  if (playlist && (shouldPrepareEpisode || override)) {
    if (seasonIndex >= 0) {
      // Longform
      const seasonIndexItem = playlist[seasonIndex];

      if (EPISODES in seasonIndexItem && nextEpisodeIndex >= 0 && nextEpisodeIndex < seasonIndexItem.episodes.length) {
        // Episodes left in season
        const nextEpisodeInfo = seasonIndexItem.episodes[nextEpisodeIndex];

        nextData = {
          ...nextData,
          vodID: nextEpisodeInfo._id || nextEpisodeInfo.id || '',
          title: nextEpisodeInfo.title,
          duration: nextEpisodeInfo.duration,
          episodeIndex: nextEpisodeIndex,
          seasonIndex,
        };
      } else if (isPrevious ? seasonIndex - 1 >= 0 : seasonIndex + 1 < playlist.length && playlist[seasonIndex + 1]) {
        // new season available
        const previousItem = playlist[seasonIndex - 1];
        const nextItem = playlist[seasonIndex + 1];

        if (EPISODES in previousItem && EPISODES in nextItem) {
          const nextEpisodeInfo = isPrevious
            ? previousItem.episodes[previousItem.episodes.length - 1]
            : nextItem.episodes[0];

          nextData = {
            ...nextData,
            vodID: nextEpisodeInfo._id || nextEpisodeInfo.id || '',
            title: nextEpisodeInfo.title,
            duration: nextEpisodeInfo.duration,
            episodeIndex: 0,
            seasonIndex: isPrevious ? seasonIndex - 1 : seasonIndex + 1,
          };
        }
      } else {
        // out of seasons and episodes
        nextData = {
          ...nextData,
          vodID: '',
          episodeIndex: -1,
          seasonIndex: -1,
        };
      }
    } else {
      // Shortform
      if (nextEpisodeIndex < playlist.length && playlist[nextEpisodeIndex]) {
        const nextEpisodeInfo = playlist[nextEpisodeIndex];

        if ('title' in nextEpisodeInfo) {
          nextData = {
            ...nextData,
            vodID: nextEpisodeInfo._id || nextEpisodeInfo.id || '',
            title: nextEpisodeInfo.title,
            duration: nextEpisodeInfo.duration,
            episodeIndex: nextEpisodeIndex,
            seasonIndex: -1,
          };
        }
      } else {
        // out of episodes
        nextData = {
          ...nextData,
          vodID: '',
          episodeIndex: -1,
          seasonIndex: -1,
        };
      }
    }
    if (!override) {
      setShowNextEpisodeOverlay(nextData.vodID !== '');
    }
    setNextEpisodeData(nextData);

    if (override) {
      return nextData;
    }
  }
};

export const onVideoProgress = (helpersData: HelpersData, progress: number, duration: string) => {
  const {
    nextEpisodeData,
    setNextEpisodeData,
    showNextEpisodeOverlay,
    setShowNextEpisodeOverlay,
    exitedShowNextEpisodeOverlay,
  } = helpersData;

  setNextEpisodeData({
    ...nextEpisodeData,
    countdown: +duration - progress,
  });
  if (!showNextEpisodeOverlay && !exitedShowNextEpisodeOverlay && progress + 8 > +duration) {
    getNextVideoData(helpersData);
  } else if (showNextEpisodeOverlay && progress + 8 <= +duration) {
    setShowNextEpisodeOverlay(false);
  }
};

const shouldPrepareNextEpisode = (helpersData: HelpersData) => {
  const { vodID, nextEpisodeData, episodeIndex, seasonIndex } = helpersData;

  return (
    nextEpisodeData.vodID === vodID &&
    nextEpisodeData.episodeIndex === episodeIndex &&
    nextEpisodeData.seasonIndex === seasonIndex
  );
};

const reloadVideo = (videoPlayerReference: MutableRefObject<VideoPlayerReference | null>) => {
  videoPlayerReference?.current?.reloadVideo?.();
};
